import { createStandaloneToast } from '@chakra-ui/react';
import { TEMPORARY } from 'helpers/constant';
import {
  DOWNLOAD_TRANSLATED_FILE_STATUS,
  TASK_STATUS,
  TRANSLATED_FILE_STATUS,
  TRANSLATION_FILE_STATUS,
} from 'helpers/enum';
import { RootState } from 'helpers/store';
import { TranslationService } from 'services/translation/translation.service';
import { call, put, select, takeEvery } from 'typed-redux-saga';
import {
  DeleteTranslationTaskAction,
  EditTranslationTaskNameAction,
  GetDetailTranslationTaskAction,
  GetTranslationTasksAction,
  UpdateTranslatedFileStatusAction,
  UpdateTranslationFileStatusAction,
} from 'types/actions/translation';
import { downloadFileBlob } from 'utils/blob';

import { translationAction } from '../actions';
import { translationTaskConstant } from '../actionTypes';

const { toast } = createStandaloneToast();

const getTranslationTasksData = function* ({ payload }: GetTranslationTasksAction) {
  try {
    const result = yield* call(() => {
      return TranslationService.getTranslationTasks(payload);
    });
    const state = yield* select((state: RootState) => state.translation);
    const tasks = state?.tasks;
    const newTasks = { ...result };

    const temporaryTasks = tasks.data.filter((task) => task.type === TEMPORARY);

    if (temporaryTasks && payload?.isKeepTemporaryTask) {
      newTasks.data = [...temporaryTasks, ...newTasks.data];
    }

    if (payload?.page) {
      newTasks.data = [...tasks.data, ...result.data];
    }
    yield* put(
      translationAction.updateTranslationTasks({
        ...newTasks,
        current_page: payload?.page ?? 0,
      })
    );
  } catch (error) {
    console.log(error);
  }
};

const getDetailTranslationTaskData = function* ({ payload }: GetDetailTranslationTaskAction) {
  try {
    const isTemporaryTranslationTask = payload?.type === TEMPORARY;
    if (isTemporaryTranslationTask) {
      // Clear current select files and load from cache
      yield* put(translationAction.clearCurrentTaskSelectedFiles());
      yield* put(translationAction.clearCurrentTaskSelectedLanguages());
      const state = yield* select((state: RootState) => state.translation);
      if (payload.task_id === state.temporary_translation_task.task_id) {
        yield* put(translationAction.loadTemporaryTaskData());
      }
    } else {
      const result = yield* call(() => {
        return TranslationService.getDetailTranslationTask({ id: payload.task_id! });
      });
      const selectedFiles = result.files.map((file) => ({
        ...file,
        translation_file_status: TRANSLATION_FILE_STATUS.UPDATED,
      }));
      const resultFiles = result.results.map((file) => ({
        ...file,
        status: file.is_error ? TRANSLATED_FILE_STATUS.ERROR : TRANSLATED_FILE_STATUS.GENERATED,
      }));

      if (payload.isWaitingProcessFileUpload === false) {
        yield* put(translationAction.clearCurrentTaskSelectedFiles());
        yield* put(translationAction.clearCurrentTaskSelectedLanguages());
      }
      yield* put(
        translationAction.updateCurrentTranslationTask({
          input_languages: [result.input_language],
          output_languages: result.output_languages,
          selectedFiles: selectedFiles,
          initialSelectedFiles: selectedFiles,
          isWaitingProcessFileUpload: false,
        })
      );
      yield* put(translationAction.updateCurrentTranslatedFiles(resultFiles));
    }

    // Store current selected files to detect files change when switch task
    yield* put(translationAction.updateInitialSelectedFiles());
  } catch (error) {
    console.log(error);
  }
};

const editTranslationTaskName = function* ({ payload }: EditTranslationTaskNameAction) {
  const state = yield* select((state: RootState) => state.translation);
  const editTranslationTask = state.tasks.data.find((task) => task.id === payload.id);

  const isTemporaryTranslationTask = editTranslationTask?.type === TEMPORARY;
  const isCurrentTranslationTask = payload.id === state.current.task_id;

  // Not allow to edit name temporary task
  if (isTemporaryTranslationTask) {
    return;
  }

  const result = yield* call(() => {
    return TranslationService.editTranslationTaskName(payload);
  });

  if (isCurrentTranslationTask) {
    const newTasks = state.tasks;
    newTasks.data = newTasks.data.filter((task) => task.id !== payload.id);

    yield* put(translationAction.updateTranslationTasks(newTasks));
  }
  toast({
    title: `Renamed to ${result.name}`,
    status: 'success',
    position: 'bottom-right',
    isClosable: true,
  });
  yield* put(translationAction.getTranslationTasks({ isKeepTemporaryTask: true }));
};

const deleteTask = function* ({ payload }: DeleteTranslationTaskAction) {
  const state = yield* select((state: RootState) => state.translation);
  const deleteTaskState = state.tasks.data.find((task) => task.id === payload.id);

  const isTemporaryTask = deleteTaskState?.type === TEMPORARY;
  const isCurrentTask = payload.id === state.current.task_id;

  if (!isTemporaryTask) {
    yield* call(() => {
      return TranslationService.deleteTranslationTask(payload);
    });
  }
  if (isCurrentTask) {
    const newTasks = state.tasks;
    newTasks.data = newTasks.data.filter((task) => task.id !== payload.id);

    yield* put(translationAction.updateTranslationTasks(newTasks));
    yield* put(
      translationAction.updateCurrentTranslationTask({ task_id: null, type: '', selectedFiles: [] })
    );
    yield* put(translationAction.updateInitialSelectedFiles());
  }
  yield* put(translationAction.getTranslationTasks({ isKeepTemporaryTask: true }));
};

const updateFileStatus = function* ({ payload }: UpdateTranslationFileStatusAction) {
  const state = yield* select((state: RootState) => state.translation);

  // Processed upload file for new task
  yield* put(
    translationAction.updateSelectedFile({
      data: { ...payload, translation_file_status: TRANSLATION_FILE_STATUS.UPDATED },
      isTemporary: state.current.type === TEMPORARY,
    })
  );
};

const updateTranslatedFileStatus = function* ({ payload }: UpdateTranslatedFileStatusAction) {
  const state = yield* select((state: RootState) => state.translation);
  const isTemporaryTranslationTask = !payload.translation_id;
  // Processed upload file for new task
  if (isTemporaryTranslationTask || payload.translation_id === state.current.task_id) {
    yield* put(
      translationAction.updateTranslatedFile({
        data: {
          ...payload,
          status: payload.error ? TRANSLATED_FILE_STATUS.ERROR : TRANSLATED_FILE_STATUS.GENERATED,
        },
        isTemporary: isTemporaryTranslationTask,
      })
    );
  }
};

const getAllLanguageDataSaga = function* () {
  try {
    const languagesData = yield* call(() => {
      return TranslationService.getListLanguages();
    });
    yield* put(translationAction.fetchAllLanguageDataSuccess(languagesData));
  } catch (error) {
    console.log(error);
  }
};

const requestTranslation = function* () {
  const { current } = yield* select((state: RootState) => state.translation);
  const isCurrentTemporaryTranslationTask = current?.type === TEMPORARY;
  yield* put(translationAction.updateTranslationStatus(TASK_STATUS.GENERATE_ANSWER));
  const { input_languages, output_languages, selectedFiles, type, task_id } = current;
  const inputLanguageCode = input_languages?.[0]?.code;

  try {
    for (const outputLanguage of output_languages) {
      for (const inputFile of selectedFiles) {
        yield* put(
          translationAction.updateTranslatedFile({
            data: {
              file_id: inputFile.id,
              output_language_code: outputLanguage.code,
              input_language_code: inputLanguageCode,
              status: TRANSLATED_FILE_STATUS.PENDING,
            },
            isTemporary: isCurrentTemporaryTranslationTask,
          })
        );
      }
    }

    const result = yield* call(() => {
      return TranslationService.addTranslationTask({
        translation_id: type !== TEMPORARY ? task_id : null,
        file_ids: selectedFiles.map((file) => file.id),
        input_language_code: input_languages[0]?.code || undefined,
        output_language_codes: output_languages.map((lang) => lang.code),
      });
    });

    // limit translation
    if (result.success === false) {
      yield* put(translationAction.getDetailTranslationTask(current));
      return;
    }

    if (isCurrentTemporaryTranslationTask) {
      // Refetch all tasks to get task data after created
      yield* put(translationAction.getTranslationTasks());
      yield* put(
        translationAction.updateCurrentTranslationTask({
          task_id: result.id,
          type: '',
        })
      );
      yield* put(translationAction.updateProcessTaskId(result.id));
    }
    yield* put(translationAction.updateInitialSelectedFiles());
  } catch (error) {
    console.log(error);
  }
};

const requestDownloadAllTranslatedFiles = function* () {
  const { current } = yield* select((state: RootState) => state.translation);
  const taskId = current?.task_id;
  yield* put(
    translationAction.updateDownloadTranslatedFilesStatus(DOWNLOAD_TRANSLATED_FILE_STATUS.PENDING)
  );
  try {
    if (taskId) {
      const result = yield* call(() => {
        return TranslationService.downloadAllTranslatedFiles({
          id: taskId,
        });
      });
      yield* call(() => {
        downloadFileBlob(result, 'translated_files');
      });
    }
  } catch (error) {
    console.log(error);
  } finally {
    yield* put(
      translationAction.updateDownloadTranslatedFilesStatus(DOWNLOAD_TRANSLATED_FILE_STATUS.IDLE)
    );
  }
};

export const watchGetLanguageData = function* () {
  yield* takeEvery(translationTaskConstant.FETCH_ALL_LANGUAGE_DATA, getAllLanguageDataSaga);
};

export const watchGetTranslationTasks = function* () {
  yield* takeEvery(translationTaskConstant.GET_TRANSLATION_TASKS, getTranslationTasksData);
};

export const watchGetDetailTranslationTask = function* () {
  yield* takeEvery(
    translationTaskConstant.GET_DETAIL_TRANSLATION_TASK,
    getDetailTranslationTaskData
  );
};

export const watchEditTranslationTaskName = function* () {
  yield* takeEvery(translationTaskConstant.EDIT_TRANSLATION_TASK_NAME, editTranslationTaskName);
};

export const watchDeleteTranslationTask = function* () {
  yield* takeEvery(translationTaskConstant.DELETE_TRANSLATION_TASK, deleteTask);
};

export const watchUpdateTranslationFileStatus = function* () {
  yield* takeEvery(translationTaskConstant.UPDATE_TRANSLATION_FILE_STATUS, updateFileStatus);
};

export const watchRequestTranslation = function* () {
  yield* takeEvery(translationTaskConstant.REQUEST_TRANSLATION, requestTranslation);
};

export const watchUpdateTranslatedFileStatus = function* () {
  yield* takeEvery(
    translationTaskConstant.UPDATE_TRANSLATED_FILE_STATUS,
    updateTranslatedFileStatus
  );
};

export const watchRequestDownloadAllTranslatedFiles = function* () {
  yield* takeEvery(
    translationTaskConstant.REQUEST_DOWNLOAD_ALL_TRANSLATED_FILES,
    requestDownloadAllTranslatedFiles
  );
};
